import React, { useCallback, useContext, useEffect, useState } from "react";

import InfoIcon from "@mui/icons-material/Info";
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { PlanType } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/admin/v1/subscriptions_pb";
import { ChargifyState } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { ChargifyPlanTypeCustomFieldValue } from "../../../../constants/index";
import SubscriptionAction from "./subscription-action";
import {
  getPlanTypeMetadata,
  setEnterpriseMetadata,
} from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { getBillingProviderName } from "helpers/subscriptions";
import { SetChargifyCustomFieldProps, UrlParamTypes } from "types/org-display";

const SetCustomField = ({
  subscription,
  planType,
}: SetChargifyCustomFieldProps) => {
  const classes = mainStyles();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const { showError } = useContext(SnackbarContext);
  const subId = subscription?.id;
  const [subPlanType, setSubPlanType] = useState(planType);
  const billingProviderName = getBillingProviderName(subscription);

  // Org with free subscription should not be able to trigger this action
  const isFreeOrg =
    subscription &&
    (subscription.type === SubscriptionType.FREE ||
      subscription.type === SubscriptionType.FREE_ORG);
  const subActive = subscription && subscription.active === true;
  const chargifyStateUnknownOrCanceled =
    subscription &&
    subscription.chargify &&
    [ChargifyState.CANCELED, ChargifyState.UNSPECIFIED].includes(
      subscription.chargify.state,
    );

  const handleSetChargifyCustomField = async () => {
    setShowLoadingSpinner(true);
    setEnterpriseMetadata(
      {
        subId: toUUID(subId),
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        setSubPlanType(PlanType.ENTERPRISE);
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
      });
  };

  const loadPlanTypeData = useCallback(() => {
    if (
      isFreeOrg === false &&
      subActive &&
      chargifyStateUnknownOrCanceled === false
    ) {
      setShowLoadingSpinner(true);
      getPlanTypeMetadata(
        {
          subId: toUUID(subId),
        },
        callOptionsHeaders(regionCode, roleId),
      )
        .then((res) => {
          setSubPlanType(res ? res.planType : 0);
        })
        .catch(showError)
        .finally(() => {
          setShowLoadingSpinner(false);
        });
    }
  }, [
    isFreeOrg,
    regionCode,
    roleId,
    showError,
    subId,
    subActive,
    chargifyStateUnknownOrCanceled,
  ]);

  useEffect(() => {
    loadPlanTypeData();
  }, [loadPlanTypeData, isFreeOrg, planType]);

  const disableSetCustomFieldBtn =
    [SubscriptionType.FREE, SubscriptionType.FREE_ORG].includes(
      subscription?.type,
    ) ||
    !subscription?.active ||
    subPlanType == PlanType.ENTERPRISE;

  const getPlanTypeLabel = (planType: PlanType) => {
    switch (planType) {
      case PlanType.UNSPECIFIED:
        return ChargifyPlanTypeCustomFieldValue.UNSPECIFIED;
      case PlanType.PREMIUM:
        return ChargifyPlanTypeCustomFieldValue.PREMIUM;
      case PlanType.ENTERPRISE:
        return ChargifyPlanTypeCustomFieldValue.ENTERPRISE;
      default:
        return ChargifyPlanTypeCustomFieldValue.UNSPECIFIED;
    }
  };

  const planTypeToolTips = () => {
    const titleMessage = `Plan Type is a ${billingProviderName} custom field to indicate the organisation's subscription plan, and will be shown on the customer's invoices. Organisation with \`Enterprise\` contracts will have \`Enterprise\` as value, default plan is \`Premium\``;
    return (
      <Tooltip title={titleMessage} data-testid="plan-type-tooltips">
        <IconButton>
          <InfoIcon />
        </IconButton>
      </Tooltip>
    );
  };

  if (isFreeOrg || !subActive || chargifyStateUnknownOrCanceled) {
    return null;
  }
  return (
    <>
      <SubscriptionAction
        title={`${billingProviderName} Plan Type:`}
        toolTip={planTypeToolTips()}
        middleElem={
          <>
            <Typography sx={classes.spaceTop}>
              {planType
                ? getPlanTypeLabel(planType)
                : getPlanTypeLabel(subPlanType)}
            </Typography>
          </>
        }
      >
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            fullWidth
            disabled={disableSetCustomFieldBtn}
            onClick={() => setOpenConfirmDialog(true)}
            data-testid="set-to-enterprise-btn"
          >
            SET TO ENTERPRISE
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Confirm Action"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => handleSetChargifyCustomField()}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
      >
        <div>
          <p>
            {" "}
            Are you sure you want set the subscription <b>Plan Type</b> (
            <i>custom field</i>) to <b>Enterprise</b> in Chargify?
          </p>

          <p>
            <Alert severity="error" variant="filled">
              <b>
                Note: this is only setting the Plan Type label to be displayed
                on the invoice, it is not converting a customer to enterprise
                product.
              </b>
            </Alert>
          </p>
        </div>
      </ConfirmDialog>
    </>
  );
};

export default SetCustomField;
